import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { apiResponse, apiError } from '../schemas/common.schema';

const c = initContract();

export const RequestResponseSchema = z.object({
  _id: z.string(),
  created_at: z.union([z.string(), z.date()]).optional(),
  request_user_name: z.string().optional(),
  request_user_phone: z.string().optional(),
  request_user_email: z.string().optional(),
  request_user_region_id: z.string().optional(),
  request_user_region_locality: z.string().optional(),
  request_user_region_name: z.string().optional(),
  request_user_region_iso: z.string().optional(),
  request_user_progress: z.any().optional(),
  request_user_notes: z.string().optional(),
  request_course_name: z.string().optional(),
  request_course_id: z.string().optional(),
  request_account_id: z.string().optional(),
  request_course_title: z.string().optional(),
  request_course_image: z.string().optional(),
  request_status: z.string().optional(),
  request_test_results: z.any().optional(),
  request_source: z.string().optional(),
  request_utm_source: z.string().optional(),
  request_utm_medium: z.string().optional(),
  request_utm_campaign: z.string().optional(),
  request_utm_content: z.string().optional(),
  request_utm_term: z.string().optional(),
  request_referrer: z.string().optional(),
  request_raw_url: z.string().optional(),
});

export const getRequestsQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().optional(),
  account_id: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  created_at: z.string().optional(),
});

export const createRequestBody = z.object({
  request_user_name: z.string(),
  request_user_phone: z.string(),
  request_user_email: z.string().optional(),
  request_user_region_id: z.string().optional(),
  request_user_region_locality: z.string().optional(),
  request_user_password: z.string().optional(),
  request_user_notes: z.string().optional(),
  request_user_progress: z.record(z.unknown()).optional(),
  request_course_name: z.string().optional(),
  request_course_id: z.string().optional(),
  request_account_id: z.string(),
  request_course_title: z.string().optional(),
  request_course_image: z.string().optional(),
  request_status: z.string().optional(),
  request_test_results: z.record(z.unknown()).optional(),
  request_source: z.string().optional(),
  request_utm_source: z.string().optional(),
  request_utm_medium: z.string().optional(),
  request_utm_campaign: z.string().optional(),
  request_utm_content: z.string().optional(),
  request_utm_term: z.string().optional(),
  request_referrer: z.string().optional(),
  request_raw_url: z.string().optional(),
});

export const requestsContract = c.router({
  getRequests: {
    method: 'GET',
    path: '/requests',
    query: getRequestsQuery,
    responses: { 200: apiResponse(z.array(RequestResponseSchema)) },
  },
  checkRequest: {
    method: 'GET',
    path: '/requests/check',
    query: z.object({
      phone: z.string().optional(),
      email: z.string().optional(),
    }),
    responses: {
      200: apiResponse(z.object({ exists: z.boolean() })),
      400: apiError,
    },
  },
  createRequest: {
    method: 'POST',
    path: '/requests',
    body: createRequestBody,
    responses: {
      200: apiResponse(RequestResponseSchema),
      400: apiError,
      429: apiError,
    },
  },

  selectCourse: {
    method: 'POST' as const,
    path: '/requests/:requestId/select-course',
    pathParams: z.object({ requestId: z.string() }),
    body: z.object({ courseId: z.string() }),
    responses: {
      200: apiResponse(z.object({ ok: z.literal(true as const), aiEnabled: z.boolean() })),
      404: apiError,
      400: apiError,
    },
  },
});

export type RequestResponseDto = z.infer<typeof RequestResponseSchema>;
export type CreateRequestBody = z.infer<typeof createRequestBody>;
